import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChampionshipsAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Plus, ArrowRight } from "lucide-react";
import { getSport } from "@/lib/sports";

export default function ChampionshipsList() {
  const [searchParams] = useSearchParams();
  const sportFilter = searchParams.get("sport");
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { settings } = useSettings();

  const championships = useMemo(() => {
    const enabled = settings?.enabled_sports;
    if (!enabled || enabled.length === 0) return all;
    return all.filter((c) => enabled.includes(c.sport || "football"));
  }, [all, settings?.enabled_sports]);

  useEffect(() => {
    setLoading(true);
    const params = sportFilter ? { sport: sportFilter } : {};
    ChampionshipsAPI.list(params)
      .then(setAll)
      .catch(() => toast.error("Impossible de charger les championnats"))
      .finally(() => setLoading(false));
  }, [sportFilter]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader showSportTabs={true} />
      <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pt-16 md:pt-24 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-4"
        >
          <div className="text-overline" style={{ color: "#D4AF37" }}>
            Archives officielles · Laser Tech
          </div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-8xl font-black uppercase tracking-tighter leading-[0.9]">
            Championnats
            <br />
            <span style={{ color: "#D4AF37" }}>Connectés</span>
          </h1>
          <p className="font-body text-base md:text-lg font-light text-zinc-400 max-w-xl">
            Le trophée conserve la mémoire de l'événement. Scanne le QR gravé →
            classement final, résultats, récompenses, photos et vidéos, pour des années.
          </p>
          <div className="pt-2">
            {user && (
              <Link to="/admin/championships/new" data-testid="cta-create-championship">
                <Button
                  className="text-black rounded-md px-5 h-11 font-body font-medium hover:opacity-90"
                  style={{ backgroundColor: "#D4AF37" }}
                >
                  <Plus className="w-4 h-4 mr-2" /> Créer un championnat
                </Button>
              </Link>
            )}
          </div>
        </motion.div>
      </section>

      <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-24">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight">
            Championnats
          </h2>
          <span className="text-overline" style={{ color: "#D4AF37" }}>
            {championships.length}
          </span>
        </div>
        {loading ? (
          <div className="text-zinc-500 font-body">Chargement...</div>
        ) : championships.length === 0 ? (
          <div
            className="border border-dashed border-white/10 rounded-xl p-10 text-center"
            data-testid="empty-championships"
          >
            <Crown className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
            <p className="font-body text-zinc-400">Aucun championnat créé.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {championships.map((c, i) => {
              const sportDef = getSport(c.sport || "football");
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Link
                    to={`/championship/${c.slug}`}
                    data-testid={`championship-card-${c.slug}`}
                    className="group block relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#111216] to-[#0A0B0E] hover:border-[#D4AF37]/60 transition-all duration-300"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      {c.cover_image_url ? (
                        <img
                          src={c.cover_image_url}
                          alt={c.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] flex items-center justify-center">
                          <Crown
                            className="w-16 h-16"
                            style={{ color: c.primary_color || "#D4AF37" }}
                          />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                      <div
                        className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20"
                        data-testid={`championship-sport-badge-${c.slug}`}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-white"
                          style={{ boxShadow: "0 0 8px #FFFFFF" }}
                        />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold metal-text">
                          {sportDef.label}
                        </span>
                      </div>
                      {c.logo_url && (
                        <div className="absolute top-3 left-3 w-12 h-12 rounded-xl overflow-hidden border border-white/20 bg-black/60 backdrop-blur-md p-1">
                          <img src={c.logo_url} alt="" className="w-full h-full object-contain" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <div
                          className="text-overline mb-1"
                          style={{ color: c.primary_color || "#D4AF37" }}
                        >
                          Saison {c.season}
                        </div>
                        <div className="font-display text-3xl font-black uppercase leading-none tracking-tight">
                          {c.name}
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex items-center justify-between border-t border-white/5">
                      <div className="text-xs font-body text-zinc-400">
                        {c.standings?.length || 0} équipes
                      </div>
                      <ArrowRight
                        className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                        style={{ color: c.primary_color || "#D4AF37" }}
                      />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-zinc-600 font-body uppercase tracking-[0.25em]">
        <img
          src="/laser-tech-logo.png"
          alt="Laser Tech"
          className="h-14 mx-auto mb-3"
          style={{ mixBlendMode: "screen" }}
        />
        Laser Tech · Création · Genève
      </footer>
    </div>
  );
}
